import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { ServiceAdviceBlock } from "@/components/ServiceAdviceBlock";
import { ServiceCautionBlock } from "@/components/ServiceCautionBlock";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { POA_FAQ_CATEGORIES } from "@/content/poa-corpus/faq-defs";
import { POA_JURISDICTION_NAMES as POA_JURISDICTIONS } from "@/content/poa-corpus/names.gen";

const ADVICE_EN = [
  "Always ask the receiving body for its own template before drafting. The Department of Lands uses form ท.ด.21 (Tor Dor 21) for land transactions, and most banks and embassies will only accept their own form.",
  "Keep the authority narrow and specific: quote the title-deed number, condominium unit number, bank account number or case number so the agent cannot act beyond your intention.",
  "Verify the principal's signing authority. For a company, the signatory must match the authorised directors shown on a DBD certificate issued within the last six months.",
  "Affix stamp duty under the Stamp Duty Schedule of the Revenue Code; the rate differs between a single agent acting once and multiple agents or repeated acts.",
  "Sign before a Notarial Services Attorney licensed by the Lawyers Council of Thailand, with two witnesses, and attach certified copies of the ID card or passport of every party.",
  "For overseas use, plan the chain first: signature certification, then certified translation, then the Legalisation Division of the Department of Consular Affairs, then the destination embassy — and allow for each queue.",
  "A mandate signed abroad for use in Thailand must be certified in the country of origin, then translated into Thai and the translation certified, before a land office, bank or court will accept it.",
  "Execute at least one spare original: land offices and banks usually keep the original in their file.",
];

const CAUTIONS_EN = [
  "Blank spaces or unsigned crossings-out are the single most common reason land offices and banks reject a mandate. Strike through every unused field and initial it.",
  "Using a generic power of attorney for a land transaction. Registration of rights and juristic acts requires form ท.ด.21 naming the transaction type and the deed number.",
  "A signature that does not match the ID card, passport or the specimen held by the bank will be refused even when everything else is correct.",
  "A mandate older than the receiving body's internal window. Many offices ask for a document signed within three to six months although Thai law sets no expiry.",
  "Missing or insufficient stamp duty weakens the document as evidence and invites objections at the counter.",
  "The mandate ends automatically on the principal's death or on a court order of incompetency; using it afterwards is a legal risk for the agent.",
  "Translating without the Department of Consular Affairs certification of the translation, where the destination requires the legalisation seal on the translation itself.",
  "Forgetting that the Apostille Convention only enters into force for Thailand on 28 February 2027; documents filed before that date still need the full embassy legalisation chain.",
];

export const Route = createFileRoute("/en/poa/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/poa-corpus");
    return { faqs: m.POA_FAQ_EN };
  },
  component: PoaFaqHubEn,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Power of Attorney in Thailand — ${faqs.length} FAQs`,
        description:
          "Power of attorney for use in and from Thailand: Department of Lands form ท.ด.21, bank mandates, estates, company mandates, child consent letters, signature certification by a Notarial Services Attorney, MFA legalisation, embassy chains, and the Apostille that enters into force for Thailand on 28 February 2027.",
        path: "/en/poa/faq",
        type: "article",
        locale: "en_US",
        alternates: [{ lang: "th", path: "/poa/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Power of Attorney FAQ", url: `${site}/en/poa/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/poa/faq")) },
      ],
    };
  },
});

function PoaFaqHubEn() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Power of Attorney FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Power of attorney in Thailand and cross-border mandates — {faqs.length} FAQs
        </h1>
        <p data-speakable="true" className="speakable-summary mb-6 text-muted-foreground">
          A power of attorney appoints an agent to act for the principal under Book 3, Title 15 (Agency), sections
          797–828 of the Thai Civil and Commercial Code. For use inside Thailand the form must match the receiving
          authority — the Department of Lands requires form ท.ด.21 for land transactions — and stamp duty follows the
          Stamp Duty Schedule of the Revenue Code. For use abroad the signature is certified by a Notarial Services
          Attorney under the Lawyers Council of Thailand regulations, then legalised by the Legalisation Division of the
          Department of Consular Affairs, then by the destination embassy. The Apostille Convention enters into force
          for Thailand on 28 February 2027. General information only; confirm current requirements with the receiving
          authority before filing.
        </p>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/poa/faq" className="text-primary underline">หนังสือมอบอำนาจข้ามประเทศ — คำถามที่พบบ่อย →</a>
        </div>

        <section aria-label="Key facts" className="mb-8 overflow-x-auto">
          <h2 className="mb-3 text-xl font-bold">Key facts about a Thai power of attorney</h2>
          <table className="w-full border-collapse text-sm">
            <tbody>
              {[
                ["Governing law", "Civil and Commercial Code, Book 3, Title 15 (Agency), ss. 797–828"],
                ["Form for land transactions", "Department of Lands power of attorney form ท.ด.21"],
                ["Stamp duty", "Stamp Duty Schedule annexed to the Revenue Code (Revenue Department)"],
                ["Who certifies signatures in Thailand", "A Notarial Services Attorney licensed by the Lawyers Council of Thailand"],
                ["Certification for overseas use", "Legalisation Division, Department of Consular Affairs, then the destination embassy"],
                ["Apostille and Thailand", "Enters into force on 28 February 2027"],
                ["Foreign documents", "Must be translated into Thai and the translation certified before filing in Thailand"],
                ["Termination", "Revocation, completion of the act, death of the principal, or a court order of incompetency"],
              ].map(([k, v]) => (
                <tr key={k} className="border-b">
                  <th scope="row" className="w-1/3 py-2 pr-3 text-left align-top font-semibold">{k}</th>
                  <td className="py-2 align-top text-muted-foreground">{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section aria-label="Categories" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">Browse by topic</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {POA_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/en/poa/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleEn}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>

        <section aria-label="Destination countries" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">Certification chain by destination</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {POA_JURISDICTIONS.map((j) => (
              <a key={j.slug} href={`/en/poa/jurisdiction/${j.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">
                {j.nameEn}
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <ServiceAdviceBlock items={ADVICE_EN} locale="en" />
        <ServiceCautionBlock items={CAUTIONS_EN} locale="en" />

        <section aria-label="Related clusters" className="mt-10">
          <h2 className="mb-3 text-xl font-bold">Related reading</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            <a href="/en/notary/faq" className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">Notarial services attorney FAQ</a>
            <a href="/en/consular-legalization/faq" className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">MFA legalisation &amp; Apostille FAQ</a>
            <a href="/en/property/faq" className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">Property FAQ</a>
            <a href="/en/inheritance/faq" className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">Wills &amp; inheritance FAQ</a>
            <a href="/en/corporate-docs/faq" className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">Corporate documents FAQ</a>
            <a href="/en/translation/faq" className="rounded-full border bg-muted/40 px-3 py-1 hover:bg-muted">Certified translation FAQ</a>
          </div>
        </section>

        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">Let us draft and sequence your mandate</h2>
          <p className="text-sm text-muted-foreground">
            Tell us the purpose, the receiving body and the destination country by phone, LINE or email, Mon-Sat
            9:00-18:00. We check the form, the scope of authority, stamp duty and the certification chain before your
            signing appointment.
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Contact us</a>
        </section>
        <FaqSourceNote topic="poa" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}